import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageCircleReply, X } from "lucide-react";

import { orpc } from "@/client";
import { TeaserResultView } from "@/components/draft/send-result-view";
import { Button } from "@/components/ui/button";
import { SendButton } from "@/components/ui/send-button";
import { OverlayShell } from "@/components/ui/overlay";
import { useConversationSession } from "@/hooks/use-conversation-session";

export function ComposerReactivation({ personId, real }: { personId: number; real: boolean }) {
	const [open, setOpen] = useState(false);
	const queryClient = useQueryClient();
	const { session, draft } = useConversationSession(personId);
	const { mutate, data, isError } = useMutation(
		orpc.send.teaser.mutationOptions({
			onSuccess: (result, _variables, revision: number) => {
				if (result.outcome === "sent" || result.outcome === "simulated") {
					session.clearSent(revision);
				}
				queryClient.invalidateQueries({ queryKey: orpc.today.queue.key() });
			},
			onMutate: () => session.state.revision,
			onSettled: () => session.patch({ reactivating: false }),
		}),
	);

	return (
		<>
			<Button data-slot="teaser" variant="ghost" size="sm" onClick={() => setOpen(true)}>
				<MessageCircleReply />
				Enviar abridor
			</Button>
			{open && (
				<OverlayShell
					label="Enviar abridor"
					onClose={() => setOpen(false)}
					className="items-center justify-center p-4"
				>
					<div className="flex w-full max-w-lg flex-col gap-4 rounded-xl bg-card p-5 shadow-overlay">
						<header className="flex items-center justify-between">
							<h2 className="font-display text-lg font-semibold">Enviar abridor</h2>
							<Button
								size="icon"
								variant="ghost"
								aria-label="Fechar"
								onClick={() => setOpen(false)}
							>
								<X />
							</Button>
						</header>
						<label className="flex flex-col gap-2 text-sm">
							Resposta após a paciente retornar
							<textarea
								value={draft.text}
								onChange={(event) => session.editText(event.target.value)}
								rows={5}
								className="rounded-lg border border-border bg-background p-3 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
							/>
						</label>
						{data && <TeaserResultView result={data} />}
						{isError && (
							<p role="alert" className="text-sm text-destructive">
								Não foi possível enviar o abridor.
							</p>
						)}
						<div className="self-end">
							<SendButton
								real={real}
								pending={draft.reactivating}
								disabled={draft.reactivating || !draft.text.trim()}
								label={draft.reactivating ? "Enviando abridor..." : "Enviar abridor"}
								onClick={() => {
									if (!session.state.reactivating) {
										session.patch({ reactivating: true });
										mutate({ person_id: personId, follow_up: draft.text });
									}
								}}
							/>
						</div>
					</div>
				</OverlayShell>
			)}
		</>
	);
}
